import {
  appRoutes,
  DEFAULT_DASHBOARD_PERIOD,
  LEAD_RESPONSE_WINDOW_HOURS,
  LEAD_STATUSES,
  type ActivitiesLink,
  type DashboardLink,
  type DashboardPeriod,
  type DealOverviewLink,
  type LeadStatus,
  type MembersLink,
  type OwnerLeadsLink,
  type PropertiesLink,
  type SalesPipelineLink,
} from '@immo/api-contract';

/**
 * The one place that knows each target list's link parameters: `dashboardLinkHref` writes them and
 * `readOwnerLeadsLink` reads them back, so a figure and the list it opens cannot drift (BL-089).
 */
export const DASHBOARD_LINK_PARAMS = {
  via: 'via',
  status: 'status',
  receivedFrom: 'receivedFrom',
  receivedTo: 'receivedTo',
  mine: 'mine',
  standort: 'standort',
  archived: 'archived',
  period: 'period',
  phase: 'phase',
  assignee: 'assignee',
  scope: 'scope',
  ids: 'ids',
  createdFrom: 'createdFrom',
  enteredPhase: 'enteredPhase',
  enteredFrom: 'enteredFrom',
  flag: 'flag',
  withoutValuation: 'withoutValuation',
  unassigned: 'unassigned',
  overdueBefore: 'overdueBefore',
  responsible: 'responsible',
  deal: 'deal',
  view: 'view',
} as const;

/** The `scope` value the Deal-Überblick reads as „every Standort", overriding the switcher's narrow. */
const SCOPE_ALL = 'all';
const IDS_SEPARATOR = ',';

const VIA_DASHBOARD = 'dashboard';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ownerLeadsQuery(link: OwnerLeadsLink, period: DashboardPeriod): string {
  const params = new URLSearchParams();

  params.set(DASHBOARD_LINK_PARAMS.via, VIA_DASHBOARD);
  link.statuses.forEach((status) => params.append(DASHBOARD_LINK_PARAMS.status, status));

  if (link.receivedFrom !== null) params.set(DASHBOARD_LINK_PARAMS.receivedFrom, link.receivedFrom);

  if (link.receivedTo !== null) params.set(DASHBOARD_LINK_PARAMS.receivedTo, link.receivedTo);

  if (link.assignedToMe) params.set(DASHBOARD_LINK_PARAMS.mine, '1');

  if (link.standortId !== null) params.set(DASHBOARD_LINK_PARAMS.standort, link.standortId);

  link.responsibleMemberIds.forEach((id) => params.append(DASHBOARD_LINK_PARAMS.responsible, id));

  if (link.statuses.includes('ARCHIVED')) params.set(DASHBOARD_LINK_PARAMS.archived, '1');

  if (period !== DEFAULT_DASHBOARD_PERIOD) params.set(DASHBOARD_LINK_PARAMS.period, period);

  return params.toString();
}

function dealOverviewQuery(link: DealOverviewLink, period: DashboardPeriod): string {
  const params = new URLSearchParams();

  params.set(DASHBOARD_LINK_PARAMS.via, VIA_DASHBOARD);
  params.set(DASHBOARD_LINK_PARAMS.view, 'list');
  params.set(DASHBOARD_LINK_PARAMS.scope, link.standortId ?? SCOPE_ALL);
  link.phases.forEach((phase) => params.append(DASHBOARD_LINK_PARAMS.phase, phase));
  link.statuses.forEach((status) => params.append(DASHBOARD_LINK_PARAMS.status, status));
  link.assigneeMemberIds.forEach((id) => params.append(DASHBOARD_LINK_PARAMS.assignee, id));
  link.attentionFlags.forEach((flag) => params.append(DASHBOARD_LINK_PARAMS.flag, flag));
  link.dealIds.forEach((id) => params.append(DASHBOARD_LINK_PARAMS.deal, id));

  if (link.createdFrom !== null) params.set(DASHBOARD_LINK_PARAMS.createdFrom, link.createdFrom);

  if (link.enteredPhase !== null) params.set(DASHBOARD_LINK_PARAMS.enteredPhase, link.enteredPhase);

  if (link.enteredFrom !== null) params.set(DASHBOARD_LINK_PARAMS.enteredFrom, link.enteredFrom);

  if (link.withoutValuation) params.set(DASHBOARD_LINK_PARAMS.withoutValuation, '1');

  if (link.unassigned) params.set(DASHBOARD_LINK_PARAMS.unassigned, '1');

  if (period !== DEFAULT_DASHBOARD_PERIOD) params.set(DASHBOARD_LINK_PARAMS.period, period);

  return params.toString();
}

function activitiesQuery(link: ActivitiesLink, period: DashboardPeriod): string {
  const params = new URLSearchParams();

  params.set(DASHBOARD_LINK_PARAMS.via, VIA_DASHBOARD);

  if (link.standortId !== null) params.set(DASHBOARD_LINK_PARAMS.standort, link.standortId);

  link.assigneeMemberIds.forEach((id) => params.append(DASHBOARD_LINK_PARAMS.assignee, id));

  if (link.dueBefore !== null) params.set(DASHBOARD_LINK_PARAMS.overdueBefore, link.dueBefore);

  if (period !== DEFAULT_DASHBOARD_PERIOD) params.set(DASHBOARD_LINK_PARAMS.period, period);

  return params.toString();
}

function propertiesQuery(link: PropertiesLink, period: DashboardPeriod): string {
  const params = new URLSearchParams();

  params.set(DASHBOARD_LINK_PARAMS.via, VIA_DASHBOARD);
  // An empty set is still written: on the API an empty `ids` means „no filter", so the list must see the link.
  params.set(DASHBOARD_LINK_PARAMS.ids, link.propertyIds.join(IDS_SEPARATOR));

  if (period !== DEFAULT_DASHBOARD_PERIOD) params.set(DASHBOARD_LINK_PARAMS.period, period);

  return params.toString().replaceAll('%2C', IDS_SEPARATOR);
}

function membersQuery(link: MembersLink, period: DashboardPeriod): string {
  const params = new URLSearchParams();

  params.set(DASHBOARD_LINK_PARAMS.via, VIA_DASHBOARD);
  // An empty set is still written: on the API an empty `ids` means „no filter", so the list must see the link.
  params.set(DASHBOARD_LINK_PARAMS.ids, link.memberIds.join(IDS_SEPARATOR));

  if (period !== DEFAULT_DASHBOARD_PERIOD) params.set(DASHBOARD_LINK_PARAMS.period, period);

  return params.toString().replaceAll('%2C', IDS_SEPARATOR);
}

function salesPipelineQuery(link: SalesPipelineLink, period: DashboardPeriod): string {
  const params = new URLSearchParams();

  params.set(DASHBOARD_LINK_PARAMS.via, VIA_DASHBOARD);

  if (link.standortId !== null) params.set(DASHBOARD_LINK_PARAMS.standort, link.standortId);

  if (link.assignedToMe) params.set(DASHBOARD_LINK_PARAMS.mine, '1');

  if (period !== DEFAULT_DASHBOARD_PERIOD) params.set(DASHBOARD_LINK_PARAMS.period, period);

  return params.toString();
}

/** The owner-leads, Deal-Überblick, Immobilien and Verkaufs-Pipeline targets carry parameters; each later slice writes its own together with its list's reader. */
export function dashboardLinkHref(link: DashboardLink, slug: string, period: DashboardPeriod): string {
  switch (link.target) {
    case 'OWNER_LEADS':
      // The route builder's second argument is the palette `?q=` term, not a query string.
      return `${appRoutes.leads(slug)}?${ownerLeadsQuery(link, period)}`;
    case 'DEAL_OVERVIEW':
      return `${appRoutes.dealOverview(slug)}?${dealOverviewQuery(link, period)}`;
    case 'PROPERTIES':
      return `${appRoutes.properties(slug)}?${propertiesQuery(link, period)}`;
    case 'ACTIVITIES':
      return `${appRoutes.activities(slug)}?${activitiesQuery(link, period)}`;
    case 'SALES_PIPELINE':
      return `${appRoutes.salesPipeline(slug)}?${salesPipelineQuery(link, period)}`;
    case 'MEMBERS':
      return `${appRoutes.members(slug)}?${membersQuery(link, period)}`;
  }
}

export function isDashboardLinkMode(params: URLSearchParams): boolean {
  return params.get(DASHBOARD_LINK_PARAMS.via) === VIA_DASHBOARD;
}

// An unparseable timestamp is dropped (BL-138); a parseable one is re-written in the form the API accepts.
export function isoOrNull(value: string | null): string | null {
  if (value === null || Number.isNaN(Date.parse(value))) return null;

  return new Date(value).toISOString();
}

/** The exact leads query a dashboard link implies, or `null` when the URL is not a dashboard link. */
export function readOwnerLeadsLink(params: URLSearchParams): Record<string, string> | null {
  if (!isDashboardLinkMode(params)) return null;

  const statuses = params
    .getAll(DASHBOARD_LINK_PARAMS.status)
    .filter((value): value is LeadStatus => (LEAD_STATUSES as ReadonlyArray<string>).includes(value));
  const receivedFrom = isoOrNull(params.get(DASHBOARD_LINK_PARAMS.receivedFrom));
  const receivedTo = isoOrNull(params.get(DASHBOARD_LINK_PARAMS.receivedTo));
  const standort = params.get(DASHBOARD_LINK_PARAMS.standort);
  const responsible = params.getAll(DASHBOARD_LINK_PARAMS.responsible).filter((id) => UUID_PATTERN.test(id));

  return {
    ...(statuses.length > 0 ? { status: statuses.join(',') } : {}),
    ...(receivedFrom !== null ? { receivedFrom } : {}),
    ...(receivedTo !== null ? { receivedTo } : {}),
    ...(params.get(DASHBOARD_LINK_PARAMS.mine) === '1' ? { assignedToMe: 'true' } : {}),
    ...(standort !== null && UUID_PATTERN.test(standort) ? { organizationIds: standort } : {}),
    ...(responsible.length > 0 ? { responsibleMemberIds: responsible.join(',') } : {}),
    ...(params.get(DASHBOARD_LINK_PARAMS.archived) === '1' ? { includeArchived: 'true' } : {}),
    sortField: 'received',
    sortDirection: 'desc',
  };
}

/**
 * What an owner-leads dashboard link filters on, in words — the chip names it instead of „aus der Übersicht
 * gefiltert". `undefined` when the link carries nothing this can phrase, so the generic chip stays.
 */
export function describeLeadsLink(
  params: URLSearchParams,
  tStatus: (key: LeadStatus) => string,
  tLink: (key: 'chip-status' | 'chip-waiting' | 'chip-received', values?: Record<string, string | number>) => string,
): string | undefined {
  const statuses = params
    .getAll(DASHBOARD_LINK_PARAMS.status)
    .filter((value): value is LeadStatus => (LEAD_STATUSES as ReadonlyArray<string>).includes(value));
  const received =
    isoOrNull(params.get(DASHBOARD_LINK_PARAMS.receivedFrom)) ??
    isoOrNull(params.get(DASHBOARD_LINK_PARAMS.receivedTo));

  if (statuses.length === 0) return received !== null ? tLink('chip-received') : undefined;

  const named = tLink('chip-status', { statuses: statuses.map((status) => tStatus(status)).join(', ') });
  // „Leads ohne Reaktion": still Neu with an arrival bound and no lower one.
  const waiting =
    statuses.length === 1 &&
    statuses[0] === 'NEW' &&
    isoOrNull(params.get(DASHBOARD_LINK_PARAMS.receivedTo)) !== null &&
    params.get(DASHBOARD_LINK_PARAMS.receivedFrom) === null;

  if (waiting) return `${named} · ${tLink('chip-waiting', { hours: LEAD_RESPONSE_WINDOW_HOURS })}`;

  return named;
}

/** The Standort and „Mir zugewiesen" a Verkaufs-Pipeline link implies, or `null` when the URL is not a dashboard link. */
export function readSalesPipelineLink(
  params: URLSearchParams,
): { readonly organizationId: string | null; readonly assignedToMe: boolean } | null {
  if (!isDashboardLinkMode(params)) return null;

  const standort = params.get(DASHBOARD_LINK_PARAMS.standort);

  return {
    organizationId: standort !== null && UUID_PATTERN.test(standort) ? standort : null,
    assignedToMe: params.get(DASHBOARD_LINK_PARAMS.mine) === '1',
  };
}

/** The Standort, assignee and overdue bound an Aktivitäten link implies, or `null` when the URL is not a dashboard link. */
export function readActivitiesLink(params: URLSearchParams): {
  readonly organizationId: string | null;
  readonly assignee: string | null;
  readonly overdueBefore: string | null;
} | null {
  if (!isDashboardLinkMode(params)) return null;

  const standort = params.get(DASHBOARD_LINK_PARAMS.standort);

  return {
    organizationId: standort !== null && UUID_PATTERN.test(standort) ? standort : null,
    assignee: params.getAll(DASHBOARD_LINK_PARAMS.assignee).find((id) => UUID_PATTERN.test(id)) ?? null,
    overdueBefore: isoOrNull(params.get(DASHBOARD_LINK_PARAMS.overdueBefore)),
  };
}

/**
 * The Objekt ids a dashboard link names, or `null` when the URL is not a dashboard link. An empty list is a link
 * to nothing, not „no filter".
 */
export function readPropertiesLink(params: URLSearchParams): string[] | null {
  if (!isDashboardLinkMode(params)) return null;

  return (params.get(DASHBOARD_LINK_PARAMS.ids) ?? '').split(IDS_SEPARATOR).filter((id) => UUID_PATTERN.test(id));
}

/** The member ids a dashboard link names, or `null` when the URL is not a dashboard link. An empty list is a link to nothing. */
export function readMembersLink(params: URLSearchParams): string[] | null {
  if (!isDashboardLinkMode(params)) return null;

  return (params.get(DASHBOARD_LINK_PARAMS.ids) ?? '').split(IDS_SEPARATOR).filter((id) => UUID_PATTERN.test(id));
}

/**
 * Drops only the parameters no control on the Deal-Überblick owns. Its `scope`, `phase`, `assignee`, `standort` and `view`
 * are controls the page itself edits, so they stay with the change applied.
 */
export function withoutLinkOnlyParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);

  [
    DASHBOARD_LINK_PARAMS.via,
    DASHBOARD_LINK_PARAMS.status,
    DASHBOARD_LINK_PARAMS.period,
    DASHBOARD_LINK_PARAMS.createdFrom,
    DASHBOARD_LINK_PARAMS.enteredPhase,
    DASHBOARD_LINK_PARAMS.enteredFrom,
    DASHBOARD_LINK_PARAMS.withoutValuation,
    DASHBOARD_LINK_PARAMS.unassigned,
    DASHBOARD_LINK_PARAMS.overdueBefore,
    DASHBOARD_LINK_PARAMS.deal,
  ].forEach((name) => next.delete(name));

  return next;
}

// The Deal-Überblick's (its `flag` and `view` controls included) and the Immobilien list's own parameters: the leads list must not delete them on leaving link mode.
const OTHER_TARGET_PARAMS: ReadonlySet<string> = new Set([
  DASHBOARD_LINK_PARAMS.phase,
  DASHBOARD_LINK_PARAMS.assignee,
  DASHBOARD_LINK_PARAMS.scope,
  DASHBOARD_LINK_PARAMS.ids,
  DASHBOARD_LINK_PARAMS.flag,
  DASHBOARD_LINK_PARAMS.view,
]);

export function withoutDashboardLinkParams(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);

  Object.values(DASHBOARD_LINK_PARAMS)
    .filter((name) => !OTHER_TARGET_PARAMS.has(name))
    .forEach((name) => next.delete(name));

  return next;
}
